import React from "react";
import {BooleanRadioButton, BooleanRadioButtonGroup} from "@octopusdeploy/design-system-components";
import {DataBaseComponent, DataBaseComponentState, Errors} from "components/DataBaseComponent";
import repository from "client/repository";
import {BusyState} from "components/BusyFromPromise/BusyFromPromise";
import {HostedInstanceResource} from "client/resources/hostedInstanceResource";
import SaveDialogLayout from "components/DialogLayout/SaveDialogLayout";
import {Text} from "components/form";
import {validateCPU, validateMemory} from "areas/instances/Instances/k8sResourceValidation";
import {HostedInstanceConfigureModularExecutionsComputeResourcesRequest} from "client/resources/hostedInstanceConfigureModularExecutionsComputeResourcesRequest";
import FormSectionHeading from "components/form/Sections/FormSectionHeading";
import { Callout } from "@octopusdeploy/design-system-components";

interface ConfigureModularExecutionsComputeResourcesDialogProps {
    errors: Errors;
    busy: BusyState;
    instance: HostedInstanceResource;
    isModularExecutionsEnabled: boolean;
    onSaveClick(): Promise<boolean>;
}

interface ConfigureModularExecutionsComputeResourcesDialogState extends DataBaseComponentState {
    request: Partial<HostedInstanceConfigureModularExecutionsComputeResourcesRequest>;
}

export class ConfigureModularExecutionsComputeResourcesDialog extends DataBaseComponent<ConfigureModularExecutionsComputeResourcesDialogProps, ConfigureModularExecutionsComputeResourcesDialogState> {
    constructor(props: ConfigureModularExecutionsComputeResourcesDialogProps) {
        super(props);
        this.state = {
            request: {
                ModellingCPURequest: this.props.instance.ModellingCPURequest,
                ModellingCPULimit: this.props.instance.ModellingCPULimit,
                ModellingMemoryRequest: this.props.instance.ModellingMemoryRequest,
                ModellingMemoryLimit: this.props.instance.ModellingMemoryLimit,
                ExecutionsCPURequest: this.props.instance.ExecutionsCPURequest,
                ExecutionsCPULimit: this.props.instance.ExecutionsCPULimit,
                ExecutionsMemoryRequest: this.props.instance.ExecutionsMemoryRequest,
                ExecutionsMemoryLimit: this.props.instance.ExecutionsMemoryLimit,
                ApplyDuringOutageWindow: true
            }
        };
    }

    render() {
        const request = this.state.request;
        return <SaveDialogLayout title={"Configure Modular Executions Compute Resources"}
                                 busy={this.state.busy}
                                 errors={this.state.errors}
                                 onSaveClick={() => this.configureModularExecutionsComputeResources(request)}>

            { !this.props.isModularExecutionsEnabled &&
                <Callout type="warning" title="Warning">
                    This instance does <strong>not</strong> have Modular Executions enabled. Changes made here will have no effect until Modular Executions is enabled.
                </Callout> }

            <BooleanRadioButtonGroup value={request.ApplyDuringOutageWindow} onChange={ApplyDuringOutageWindow => this.updateRequest({ApplyDuringOutageWindow})}>
                <BooleanRadioButton value={true} label="Apply During Maintenance Window" />
                <BooleanRadioButton value={false} label="Apply Immediately" />
            </BooleanRadioButtonGroup>

            <FormSectionHeading title="Modelling Resources" />

            <Text label="Modelling CPU Request"
                  value={request.ModellingCPURequest}
                  onChange={ModellingCPURequest => this.updateRequest({ModellingCPURequest})}
                  validate={validateCPU}
                  hintText="CPU request for Modelling pod (e.g., 100m, 0.5)" />

            <Text label="Modelling CPU Limit"
                  value={request.ModellingCPULimit}
                  onChange={ModellingCPULimit => this.updateRequest({ModellingCPULimit})}
                  validate={validateCPU}
                  hintText="CPU limit for Modelling pod (e.g., 1000m, 1)" />

            <Text label="Modelling Memory Request"
                  value={request.ModellingMemoryRequest}
                  onChange={ModellingMemoryRequest => this.updateRequest({ModellingMemoryRequest})}
                  validate={validateMemory}
                  hintText="Memory request for Modelling pod (e.g., 800Mi, 1Gi)" />

            <Text label="Modelling Memory Limit"
                  value={request.ModellingMemoryLimit}
                  onChange={ModellingMemoryLimit => this.updateRequest({ModellingMemoryLimit})}
                  validate={validateMemory}
                  hintText="Memory limit for Modelling pod (e.g., 1Gi, 2G)" />

            <FormSectionHeading title="Executions Resources" />

            <Text label="Executions CPU Request"
                  value={request.ExecutionsCPURequest}
                  onChange={ExecutionsCPURequest => this.updateRequest({ExecutionsCPURequest})}
                  validate={validateCPU}
                  hintText="CPU request for Executions pod (e.g., 100m, 0.5)" />

            <Text label="Executions CPU Limit"
                  value={request.ExecutionsCPULimit}
                  onChange={ExecutionsCPULimit => this.updateRequest({ExecutionsCPULimit})}
                  validate={validateCPU}
                  hintText="CPU limit for Executions pod (e.g., 1000m, 1)" />

            <Text label="Executions Memory Request"
                  value={request.ExecutionsMemoryRequest}
                  onChange={ExecutionsMemoryRequest => this.updateRequest({ExecutionsMemoryRequest})}
                  validate={validateMemory}
                  hintText="Memory request for Executions pod (e.g., 800Mi, 1Gi)" />

            <Text label="Executions Memory Limit"
                  value={request.ExecutionsMemoryLimit}
                  onChange={ExecutionsMemoryLimit => this.updateRequest({ExecutionsMemoryLimit})}
                  validate={validateMemory}
                  hintText="Memory limit for Executions pod (e.g., 1Gi, 2G)" />

        </SaveDialogLayout>;
    }

    private updateRequest(update: Partial<HostedInstanceConfigureModularExecutionsComputeResourcesRequest>) {
        return this.setState(prevState => ({request: {...prevState.request, ...update}}));
    }

    private configureModularExecutionsComputeResources(request: Partial<HostedInstanceConfigureModularExecutionsComputeResourcesRequest>) {
        return this.doBusyTask(async () => {
            await repository.HostedInstances.configureModularExecutionsComputeResources(this.props.instance, request as HostedInstanceConfigureModularExecutionsComputeResourcesRequest);
            await this.props.onSaveClick();
            return true;
        });
    }
}
